import { Text, VStack, Checkbox } from "flicket-ui";
import { useFormikContext } from "formik";
import * as Yup from "yup";
import { MobileDivider } from "./MobileDivider";
import { marketingOptInCopy } from "~config";
import { useAccount, useOrganization } from "~hooks";
import { useOrderContext } from "../hooks/OrderContext";
import { useMemo } from "react";

export interface FormValues {
  usableForMarketing: boolean;
}

export const useInitialValues = (values?: FormValues): FormValues => {
  const { user } = useAccount();
  const { containsOnlyChildTickets } = useOrderContext();
  const { organization } = useOrganization();

  // Logic taken from src/components/checkout/checkout.Payment.tsx:~716
  const usableForMarketingDefault = useMemo(() => {
    //  TODO: confirm user logic is correct
    const userUseable = Boolean(
      user?.usableForEmailMarketing || user?.usableForSMSMarketing
    );
    return (
      !containsOnlyChildTickets &&
      userUseable &&
      !NZCIds.includes(organization.id) &&
      !!organization?.defaultOptInMarketing
    );
  }, [
    containsOnlyChildTickets,
    organization.id,
    organization?.defaultOptInMarketing,
    user,
  ]);

  return {
    usableForMarketing: usableForMarketingDefault,
    ...values,
  };
};

export const schema: Yup.ObjectSchema<FormValues> = Yup.object({
  usableForMarketing: Yup.boolean().required(),
});

// Taken from src/components/checkout/checkout.Payment.tsx:~522
// Temp array to allow for nzc specific functionality
export const NZCIds = [
  "cd5d54fb-c135-4e44-8b17-940c6dbcc3b4",
  "94c90d7d-456a-42f3-9984-c21f199e1f22",
  "638c6c08-517a-4254-9d25-6102c9d860b9",
  "925f4dc5-9953-4cf7-9527-f984439ad301",
  "5508ed88-4a86-4d5d-b59e-4256555c99dc",
  "8bfd0695-127e-4ad7-8515-23af25a8ea97",
  "62c7adc1-7e96-44e1-a896-051236206e17",
  "e7d15cb8-bfea-4e39-bfbf-3b6785b2b261",
];

export const OptInToMarketingFormPartial = () => {
  const { containsOnlyChildTickets, isBackOfficeOrder } = useOrderContext();

  const { organization } = useOrganization();
  const formik = useFormikContext<FormValues>();

  // src/components/checkout/checkout.Payment.tsx:519
  let optInCopy = marketingOptInCopy(organization, "full");

  if (NZCIds.includes(organization.id)) {
    // Remove period
    if (optInCopy.lastIndexOf(".") === optInCopy.length - 1) {
      optInCopy = optInCopy.substring(0, optInCopy.length - 1);
    }

    optInCopy = `${optInCopy} & New Zealand Cricket.`;
  }

  // Good Food And Wine Show
  if (organization.id === "9df869da-6b47-4488-b4d9-e91eab557294") {
    optInCopy = `By opting in to receive communications from the Good Food & Wine Show (GFWS), you consent to your personal information
    being collected and shared with GFWS and selected partners for marketing and promotional purposes. You can unsubscribe at any time.`;
  }

  if (isBackOfficeOrder) {
    return null;
  }

  return (
    <>
      <VStack gap={1}>
        <Text variant="header.S">Stay in the loop</Text>
        <Checkbox
          variant="card"
          label={optInCopy}
          checked={formik.values.usableForMarketing}
          onChange={(e) => {
            void formik.setFieldValue("usableForMarketing", e.target.checked);
          }}
          disabled={formik.isSubmitting || containsOnlyChildTickets}
        />
        {containsOnlyChildTickets && (
          <Text variant="small" textAlign="left">
            This order contains only child tickets, so you cannot opt in to
            marketing.
          </Text>
        )}
      </VStack>
      <MobileDivider />
    </>
  );
};
